// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'
import { toAdminListParams } from '@/core/lib/table-query'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  teamDetailQueryOptions,
  teamListQueryOptions,
} from '@/modules/team/team.hooks'
import { teamSearchSchema } from '@/modules/team/team.schemas'
import type { TTeamDetail } from '@/modules/team/team.types'

export const teamRoute = createRoute({
  path: '/teams',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const teamLogoRequestsRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: 'logo-requests',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'teams.list',
      'Takım logo isteklerine erişim izniniz yok.'
    )
  },
  head: () => ({
    meta: [
      {
        title: 'Takım Logo İstekleri',
      },
    ],
  }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/team/pages/team-logo-requests.page')
  ),
})

export const teamListRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: '/',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'teams.list',
      'Takım listesine erişim izniniz yok.'
    )
  },
  validateSearch: teamSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    await queryClient.ensureQueryData(
      teamListQueryOptions(toAdminListParams(deps))
    )
  },
  head: () => ({
    meta: [
      {
        title: 'Takım Listesi',
      },
    ],
  }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/team/pages/list-team.page')
  ),
})

export const teamDetailRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: '$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'teams.detail',
      'Takım detayına erişim izniniz yok.'
    )
  },
  head: ({ params: { id } }) => {
    const team = queryClient.getQueryData<TTeamDetail>(['teamDetail', id])

    return {
      meta: [
        {
          title: `Takım | ${team?.name ?? id}`,
        },
      ],
    }
  },
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(teamDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/team/pages/team-detail.page')
  ),
})

export const teamEditRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: '$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'teams.edit',
      'Takım durumu düzenleme izniniz yok.'
    )
  },
  head: () => ({
    meta: [
      {
        title: 'Takım Durumu Düzenle',
      },
    ],
  }),
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(teamDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/team/pages/edit-team.page')
  ),
})
